import type { HubConnection } from "@microsoft/signalr";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { clientCodeHeader } from "@entrilia/shared/api/clientApiContext";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useSignalR from "@entrilia/shared/hooks/useSignalR";
import { logError } from "@entrilia/shared/logging";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { ClientProfileApiResponse } from "../../../api/clientsManagementTypes";
import type { OrganizationUserInfo } from "../../../api/clientUsersTypes";
import { api } from "../../../api/operationsApi";
import { smartViewsTraceHubUrl } from "../../../api/smartViewsTraceApi";
import type { CapturedBatch, QueryTracePayload } from "../../../api/smartViewsTraceTypes";
import UserContext from "../../../context/UserContext";
import { getEntriliaOrgClientCode } from "../../../utilities/clientHelper";
import TraceBatchCard from "./TraceBatchCard";

const startTrace = withErrorHandling(api.smartViewsTrace.start);
const stopTrace = withErrorHandling(api.smartViewsTrace.stop);
const setTraceOptions = withErrorHandling(api.smartViewsTrace.setOptions);

const MaxBatches = 200;
const QueryTraceEvent = "queryTraceCaptured";

interface ActiveTarget {
  clientCode: string;
  userEmail: string;
}

const getPrimaryOrganizationCode = (
  fundManager: ClientProfileApiResponse,
  organizationCodes: Set<string>
): string | undefined => {
  const entriliaOrgCode = getEntriliaOrgClientCode();
  const specificOrg = fundManager.managedByClients.find(
    (code) => code !== entriliaOrgCode && organizationCodes.has(code)
  );
  return specificOrg ?? fundManager.managedByClients.find((code) => organizationCodes.has(code));
};

const SmartViewsTraceContent = () => {
  const [clients, clientsError] = useFetch(api.clientProfiles.getClientProfiles);
  const { userInfo } = useDefinedContext(UserContext);

  const [selectedFundManager, setSelectedFundManager] = useState<ClientProfileApiResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<OrganizationUserInfo | null>(null);

  const fundAdminsByCode = useMemo(() => {
    const map = new Map<string, ClientProfileApiResponse>();
    (clients ?? []).forEach((c) => {
      if (c.clientType === "FundAdmin") {
        map.set(c.clientCode, c);
      }
    });
    return map;
  }, [clients]);

  const fundAdminCodes = useMemo(() => new Set(fundAdminsByCode.keys()), [fundAdminsByCode]);

  const fundAdminGroupOf = useCallback(
    (fundManager: ClientProfileApiResponse) => {
      const orgCode = getPrimaryOrganizationCode(fundManager, fundAdminCodes);
      const fundAdmin = orgCode ? fundAdminsByCode.get(orgCode) : undefined;
      return fundAdmin ? `${fundAdmin.clientName} (${fundAdmin.clientCode})` : "Other";
    },
    [fundAdminCodes, fundAdminsByCode]
  );

  const fundManagerOptions = useMemo(
    () =>
      (clients ?? [])
        .filter((c) => c.clientType === "FundManager")
        .sort(
          (a, b) => fundAdminGroupOf(a).localeCompare(fundAdminGroupOf(b)) || a.clientName.localeCompare(b.clientName)
        ),
    [clients, fundAdminGroupOf]
  );
  const [includeResults, setIncludeResults] = useState(false);

  const [activeTarget, setActiveTarget] = useState<ActiveTarget | null>(null);
  const [expiresAtUtc, setExpiresAtUtc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [batches, setBatches] = useState<CapturedBatch[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const tracing = activeTarget !== null;

  const selectedFundManagerCode = selectedFundManager?.clientCode ?? "";
  const usersApiCall = useCallback(
    () => api.clientUsers.getOrganizationUsers({ clientCode: selectedFundManagerCode }),
    [selectedFundManagerCode]
  );
  const [usersResponse, usersError, { isFetching: usersLoading }] = useFetch(usersApiCall, {
    disabled: selectedFundManager === null,
    onSetData: (data) => {
      const currentEmails = [userInfo.email, userInfo.signInEmail].filter(Boolean).map((email) => email.toLowerCase());
      const currentUser = data.users.find((u) => currentEmails.includes(u.email.toLowerCase()));
      if (currentUser) {
        setSelectedUser(currentUser);
      }
    },
  });
  const users = usersResponse?.users ?? [];

  const appendBatch = useCallback((payload: QueryTracePayload) => {
    setBatches((prev) => [{ ...payload, id: crypto.randomUUID() }, ...prev].slice(0, MaxBatches));
  }, []);

  const subscriptions = useMemo(() => [{ eventName: QueryTraceEvent, callback: appendBatch }], [appendBatch]);

  const onConnected = useCallback(
    async (connection: HubConnection) => {
      if (!activeTarget) {
        return;
      }
      try {
        const response = await api.smartViewsTrace.getSubscriptionToken(
          activeTarget.clientCode,
          activeTarget.userEmail
        );
        if (response.success && response.data) {
          await connection.invoke("Subscribe", response.data);
        }
      } catch (e) {
        logError(e, "[SmartViewsTrace] Failed to subscribe to query trace updates");
      }
    },
    [activeTarget]
  );

  useSignalR({
    url: smartViewsTraceHubUrl,
    subscriptions,
    customHeaders: clientCodeHeader(),
    onConnected,
    filter: () => tracing,
  });

  const handleStart = async () => {
    if (!selectedFundManager || !selectedUser) {
      setError("Select a fund manager and a user.");
      return;
    }

    const target: ActiveTarget = { clientCode: selectedFundManager.clientCode, userEmail: selectedUser.email };
    setError(undefined);
    setBusy(true);
    const [data, err] = await startTrace({ ...target, includeResults });
    setBusy(false);

    if (err || !data) {
      setError(err?.message ?? "Failed to start trace.");
      return;
    }

    setBatches([]);
    setExpiresAtUtc(data.expiresAtUtc);
    setActiveTarget(target);
  };

  const handleStop = async () => {
    if (!activeTarget) {
      return;
    }
    setBusy(true);
    const [, err] = await stopTrace(activeTarget.clientCode, activeTarget.userEmail);
    setBusy(false);
    if (err) {
      setError(err.message);
    }
    setActiveTarget(null);
    setExpiresAtUtc(null);
  };

  const handleToggleIncludeResults = async (value: boolean) => {
    setIncludeResults(value);
    if (!activeTarget) {
      return;
    }
    const [, err] = await setTraceOptions(activeTarget.clientCode, activeTarget.userEmail, value);
    if (err) {
      setError(err.message);
      setIncludeResults(!value);
    }
  };

  const totalQueries = batches.reduce((sum, batch) => sum + batch.queries.length, 0);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredBatches = useMemo(() => {
    if (!normalizedSearch) {
      return batches.map((batch) => ({ batch, queries: batch.queries }));
    }
    return batches
      .map((batch) => {
        const batchMatches =
          (batch.requestPath ?? "").toLowerCase().includes(normalizedSearch) ||
          batch.reportUsage.toLowerCase().includes(normalizedSearch);
        const queries = batchMatches
          ? batch.queries
          : batch.queries.filter((query) => query.queryText.toLowerCase().includes(normalizedSearch));
        return { batch, queries };
      })
      .filter((entry) => entry.queries.length > 0);
  }, [batches, normalizedSearch]);

  const shownQueries = filteredBatches.reduce((sum, entry) => sum + entry.queries.length, 0);

  return (
    <Stack spacing={3} sx={{ minHeight: 0, flex: 1 }}>
      <Stack spacing={2} sx={{ width: "32rem" }}>
        <Typography variant="subtitle1">SmartViews Query Trace</Typography>
        <Typography variant="caption" color="textSecondary">
          Capture every BI SQL query a target user runs, with parameters inlined, in real time. Trace auto-expires after
          20 minutes.
        </Typography>

        {clientsError && <Alert severity="error">Failed to load clients: {clientsError.message}</Alert>}

        <Autocomplete
          options={fundManagerOptions}
          loading={clients === undefined && !clientsError}
          groupBy={(option) => fundAdminGroupOf(option)}
          value={selectedFundManager}
          onChange={(_, value) => {
            setSelectedFundManager(value);
            setSelectedUser(null);
          }}
          getOptionLabel={(option) => `${option.clientName} (${option.clientCode})`}
          isOptionEqualToValue={(option, value) => option.clientCode === value.clientCode}
          disabled={tracing}
          renderInput={(params) => <TextField {...params} label="Fund Manager" />}
        />

        <Autocomplete
          options={users}
          loading={usersLoading}
          value={selectedUser}
          onChange={(_, value) => setSelectedUser(value)}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          isOptionEqualToValue={(option, value) => option.email === value.email}
          disabled={tracing || selectedFundManager === null}
          noOptionsText={selectedFundManager === null ? "Select a fund manager first" : "No users found"}
          renderInput={(params) => (
            <TextField {...params} label="User" error={usersError !== undefined} helperText={usersError?.message} />
          )}
        />

        <FormControlLabel
          control={<Checkbox checked={includeResults} onChange={(_, value) => handleToggleIncludeResults(value)} />}
          label="Include query results (full payload)"
        />

        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          {!tracing ? (
            <Button
              variant="contained"
              loading={busy}
              disabled={!selectedFundManager || !selectedUser}
              onClick={handleStart}
            >
              Start Trace
            </Button>
          ) : (
            <Button variant="outlined" color="error" loading={busy} onClick={handleStop}>
              Stop Trace
            </Button>
          )}
          {tracing && (
            <Typography variant="caption" sx={{ color: "success.main" }}>
              Tracing {activeTarget.userEmail} @ {activeTarget.clientCode}
              {expiresAtUtc ? ` · expires ${convertISODateTime(expiresAtUtc)}` : ""}
            </Typography>
          )}
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError(undefined)}>
            {error}
          </Alert>
        )}
      </Stack>
      <Divider />
      <Stack spacing={1} sx={{ minHeight: 0, flex: 1 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <SearchField placeholder="Filter by SQL, endpoint, or usage" debounceTimeMs={300} onSearch={setSearchTerm} />
          <Typography color="secondary">
            {normalizedSearch
              ? `${shownQueries} of ${totalQueries} ${totalQueries === 1 ? "query" : "queries"}`
              : `${totalQueries} ${totalQueries === 1 ? "query" : "queries"} captured`}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => setBatches([])}
            disabled={batches.length === 0}
            sx={{ ml: "auto" }}
          >
            Clear
          </Button>
        </Stack>
        {normalizedSearch && filteredBatches.length === 0 && (
          <Typography variant="caption" color="textSecondary">
            No queries match the filter.
          </Typography>
        )}
        <Stack spacing={2}>
          {filteredBatches.map(({ batch, queries }) => (
            <TraceBatchCard key={batch.id} batch={batch} queries={queries} />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SmartViewsTraceContent;
